import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  Stack,
  TextField,
  Button,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Paper,
  CircularProgress,
  Alert,
  TableSortLabel,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import moment from 'moment';
import { toast } from 'react-toastify';
import Services from 'src/services/Services';

const mockDiscountTypes = ['Oran', 'Miktar'];

const initialFormData = {
  couponName: '',
  location: '',
  userFullName: '',
  userPhone: '',
  startDate: null,
  endDate: null,
  discountType: '',
  discountValue: '',
  maxUsage: '',
  couponCode: '',
  bulkQuantity: '',
};

const generateRandomCode = (length = 8) => {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

export default function BoxOperationsCouponPage() {
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10); // YENİ: Sayfa başına satır
  const [locations, setLocations] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [filterText, setFilterText] = useState('');

  const [isCreateModalOpen, setCreateModalOpen] = useState(false);
  const [couponType, setCouponType] = useState('Bireysel');
  const [isBulkCreate, setIsBulkCreate] = useState(false);
  const [isSuccessModalOpen, setSuccessModalOpen] = useState(false);
  const [generatedCodes, setGeneratedCodes] = useState([]);

  const [formData, setFormData] = useState(initialFormData);
  const [isBireyselConfirmOpen, setBireyselConfirmOpen] = useState(false);
  const [isBireyselSuccessOpen, setBireyselSuccessOpen] = useState(false);


  const [smsInfo, setSmsInfo] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'startDate', direction: 'desc' });

  const handleSortRequest = (key) => {
    let direction = 'asc';
    // Eğer aynı başlığa tekrar tıklandıysa ve yön 'asc' ise, yönü 'desc' yap
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const fetchCoupons = async () => {
    setLoading(true);
    setError(null);
    try {
      const [couponsResponse, locationsResponse] = await Promise.all([
        Services.GetAllCampaignCoupons(),
        Services.GetDropboxLockerLocations(),
      ]);
      setCoupons(couponsResponse.data || []);
      setLocations(locationsResponse.data.result || []);
    } catch (err) {
      console.error('Failed to fetch coupons:', err);
      setError('Kampanyalar yüklenirken bir hata oluştu.');
      toast.error('Kampanyalar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, []);

  const filteredCoupons = useMemo(() => {
    if (!coupons) {
      return [];
    }

    let sortableItems = [...coupons];

    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        const aValue = new Date(a[sortConfig.key]);
        const bValue = new Date(b[sortConfig.key]);

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems.filter((coupon) => {
      const campaignName = coupon.campaignName || '';
      const locationCode = coupon.locationCode || '';
      return (
        campaignName.toLowerCase().includes(filterText.toLowerCase()) ||
        locationCode.toLowerCase().includes(filterText.toLowerCase())
      );
    });

  }, [coupons, filterText, sortConfig]);

  const isFormValid = useMemo(() => {
    const commonFields =
      formData.couponName &&
      formData.location &&
      formData.startDate &&
      formData.endDate &&
      formData.discountType &&
      formData.discountValue &&
      formData.maxUsage;
    if (!commonFields) return false;
    if (couponType === 'Bireysel') {
      return formData.userFullName && formData.userPhone && formData.couponCode;
    }
    if (couponType === 'Kurumsal') {
      return isBulkCreate ? formData.bulkQuantity : formData.couponCode;
    }
    return false;
  }, [formData, couponType, isBulkCreate]);

  const handleOpenCreateModal = () => {
    setFormData(initialFormData);
    setCouponType('Bireysel');
    setIsBulkCreate(false);
    setCreateModalOpen(true);
  };
  const handleCloseCreateModal = () => setCreateModalOpen(false);

  // ✅ YENİ handleCreateCoupon (Sadece Kupon Oluşturur)
  const handleCreateCoupon = async () => {
    if (submitting) return;
    if (!isFormValid) return;

    setSubmitting(true);

    try {
      if (couponType === 'Kurumsal') {
        if (isBulkCreate) {
          const quantity = parseInt(formData.bulkQuantity, 10);
          if (isNaN(quantity) || quantity <= 0) {
            toast.error('Lütfen toplu oluşturma için geçerli bir adet girin.');
            setSubmitting(false); return;
          }
          const createdCodes = [];
          const basePayload = { OperationType: 'Cari', StartDate: moment(formData.startDate).format('YYYY-MM-DD'), EndDate: moment(formData.endDate).format('YYYY-MM-DD'), DiscountType: formData.discountType === 'Oran' ? 'Rate' : 'Amount', UseType: 'Special', MaxUse: formData.maxUsage, UseCount: 0, CampaignName: formData.couponName, RateAmount: formData.discountValue, Status: true, LocationCode: formData.location, GSMEmail: '' };
          for (let i = 0; i < quantity; i++) {
            const newCode = generateRandomCode();
            const payload = { ...basePayload, Code: newCode };
            try { await Services.CreateCampaign(payload); createdCodes.push(newCode); } catch (err) { toast.warn(`Oluşturulan kuponlardan birinde hata oluştu: ${newCode}`); }
          }
          if (createdCodes.length > 0) { toast.success(`${createdCodes.length} adet kupon başarıyla oluşturuldu.`); setGeneratedCodes(createdCodes); setSuccessModalOpen(true); } else { toast.error('Hiçbir kupon oluşturulamadı.'); }
        } else {
          const payload = { OperationType: 'Cari', StartDate: moment(formData.startDate).format('YYYY-MM-DD'), EndDate: moment(formData.endDate).format('YYYY-MM-DD'), DiscountType: formData.discountType === 'Oran' ? 'Rate' : 'Amount', UseType: 'Special', MaxUse: formData.maxUsage, UseCount: 0, CampaignName: formData.couponName, Code: formData.couponCode, RateAmount: formData.discountValue, Status: true, LocationCode: formData.location, GSMEmail: '' };
          await Services.CreateCampaign(payload);
          toast.success(`'${payload.CampaignName}' kampanyası başarıyla oluşturuldu.`);
          setGeneratedCodes([payload.Code]);
          setSuccessModalOpen(true);
        }
      }
      else if (couponType === 'Bireysel') {
        const payload = {
          OperationType: "Bireysel", StartDate: moment(formData.startDate).format("YYYY-MM-DD"), EndDate: moment(formData.endDate).format("YYYY-MM-DD"), DiscountType: formData.discountType === 'Oran' ? "Rate" : "Amount", UseType: "Special", MaxUse: parseInt(formData.maxUsage, 10), UseCount: 0, CampaignName: formData.couponName, Code: formData.couponCode, RateAmount: parseFloat(formData.discountValue), Status: true, LocationCode: formData.location, GSMEmail: formData.userPhone,
        };

        await Services.CreateCampaign(payload);
        toast.info("Kupon başarıyla oluşturuldu. Şimdi SMS gönderimini onaylayın.");
        setSmsInfo({ ...formData });

        // SMS Onay Modalını aç
        setBireyselConfirmOpen(true);
      }
      handleCloseCreateModal();
      fetchCoupons();

    } catch (error) {
      console.error("Kupon oluşturma hatası:", error);
      toast.error("Kupon oluşturulurken bir hata oluştu.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmBireyselSend = async () => {
    if (submitting || !smsInfo) return;

    setSubmitting(true);

    try {
      const message = `Sayın ${smsInfo.userFullName}, '${smsInfo.couponName}' kampanyasına özel kupon kodunuz: ${smsInfo.couponCode}. Bitiş Tarihi: ${moment(smsInfo.endDate).format('DD.MM.YYYY')}.`;
      await Services.BoxOperationSendingSMS(smsInfo.userPhone, message);
      toast.success("SMS başarıyla gönderildi.");
      setBireyselSuccessOpen(true);
    } catch (smsError) {
      console.error("SMS gönderim hatası:", smsError);
      toast.error("SMS gönderilirken bir hata oluştu!");
    } finally {
      setBireyselConfirmOpen(false);
      setSmsInfo(null);
      setSubmitting(false);
    }
  };

  const handleDeleteCoupon = async (couponId, couponName) => {
    if (window.confirm(`'${couponName}' kampanyasını silmek istediğinizden emin misiniz?`)) {
      setSubmitting(true);
      try {
        await Services.DeleteCampaign(couponId);
        toast.success('Kampanya başarıyla silindi.');
        fetchCoupons();
      } catch (err) {
        console.error('Failed to delete coupon:', err);
        toast.error('Kampanya silinirken bir hata oluştu.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handlePhoneChange = (e) => {
    const numericValue = e.target.value.replace(/[^0-9]/g, '');
    if (numericValue.length <= 10) {
      setFormData({ ...formData, userPhone: numericValue });
    }
  };

  const handleCloseSuccessModal = () => setSuccessModalOpen(false);
  const handleCloseBireyselSuccessModal = () => setBireyselSuccessOpen(false);
  const handleCopyCodes = () => navigator.clipboard.writeText(generatedCodes.join('\n'));

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Container maxWidth={false} sx={{ mt: 2, mb: 4, maxWidth: '1720px' }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h5" fontWeight="bold">
            Kampanyalar
          </Typography>
          <Button
            variant="contained"
            onClick={handleOpenCreateModal}
            sx={{
              backgroundColor: '#D92D20',
              '&:hover': { backgroundColor: '#B42318' },
              borderRadius: '8px',
              textTransform: 'none',
              px: 2,
            }}
            startIcon={<AddIcon />}
          >
            Kupon Oluştur
          </Button>
        </Stack>
        <Card sx={{ boxShadow: 'none', border: '1px solid #EAECF0' }}>
          <Box sx={{ p: 2, borderBottom: '1px solid #EAECF0' }}>
            <Stack direction="row" justifyContent="space-between">
              <TextField
                size="small"
                placeholder="Kampanya adı veya Lokasyon Kodu ara..."
                value={filterText}
                onChange={(e) => setFilterText(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' }, minWidth: '320px' }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
          </Box>
          <TableContainer>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>
            ) : (
              <Table sx={{ minWidth: 1800 }}>
                <TableHead sx={{ backgroundColor: '#F9FAFB' }}>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>LOKASYON KODU</TableCell>
                    <TableCell>KAMPANYA ADI</TableCell>
                    <TableCell>KULLANIM SAYISI</TableCell>
                    <TableCell>MAKS. KULLANIM</TableCell>
                    <TableCell>İNDİRİM TİPİ</TableCell>
                    <TableCell>ORAN / MİKTAR</TableCell>
                    <TableCell sortDirection={sortConfig.key === 'startDate' ? sortConfig.direction : false}>
                      <TableSortLabel
                        active={sortConfig.key === 'startDate'}
                        direction={sortConfig.key === 'startDate' ? sortConfig.direction : 'asc'}
                        onClick={() => handleSortRequest('startDate')}
                      >
                        BAŞLANGIÇ TARİHİ
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>BİTİŞ TARİHİ</TableCell>
                    <TableCell>KULLANIM TİPİ</TableCell>
                    <TableCell>KUPON KODU</TableCell>
                    <TableCell>DURUM</TableCell>
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCoupons
                    .slice((page - 1) * rowsPerPage, page * rowsPerPage) // GÜNCELLENDİ
                    .map((row) => (
                      <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                        <TableCell>{row.id}</TableCell>
                        <TableCell>{row.locationCode}</TableCell>
                        <TableCell>{row.campaignName}</TableCell>
                        <TableCell>{row.useCount}</TableCell>
                        <TableCell>{row.maxUse}</TableCell>
                        <TableCell>{row.discountType === 'Rate' ? 'Oran' : 'Miktar'}</TableCell>
                        <TableCell>{row.rateAmount}</TableCell>
                        <TableCell>{moment(row.startDate).format('DD.MM.YYYY')}</TableCell>
                        <TableCell>{moment(row.endDate).format('DD.MM.YYYY')}</TableCell>
                        <TableCell>{row.operationType}</TableCell>
                        <TableCell>
                          <Chip
                            icon={<LockOutlinedIcon />}
                            label={row.code}
                            size="small"
                            sx={{
                              backgroundColor: '#FEF3F2',
                              color: '#B42318',
                              fontWeight: 500,
                              '& .MuiChip-icon': { color: '#B42318' },
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <FiberManualRecordIcon
                            sx={{
                              fontSize: 14,
                              color: row.status ? '#12B76A' : '#F04438',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Button
                            startIcon={<DeleteOutlineIcon />}
                            sx={{
                              color: '#B42318',
                              textTransform: 'none',
                              '&:hover': { backgroundColor: 'transparent' },
                            }}
                            onClick={() => handleDeleteCoupon(row.id, row.campaignName)}
                            disabled={submitting}
                          >
                            Kaldır
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {/* GÜNCELLENDİ: Tüm sayfalama bölümü */}
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ p: 2 }}
          >
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2">Sayfa başına satır:</Typography>
              <TextField
                select
                size="small"
                value={rowsPerPage}
                onChange={(e) => {
                  setRowsPerPage(Number(e.target.value));
                  setPage(1);
                }}
                sx={{ '& .MuiInputBase-root': { borderRadius: '6px' } }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
              </TextField>
            </Stack>

            <Stack direction="row" alignItems="center" spacing={2}>
              <Typography variant="body2">
                Toplam {filteredCoupons.length} kayıttan {Math.min(((page - 1) * rowsPerPage) + 1, filteredCoupons.length)}–{Math.min(page * rowsPerPage, filteredCoupons.length)} arası gösteriliyor.
              </Typography>
              <Pagination
                count={Math.ceil(filteredCoupons.length / rowsPerPage)}
                page={page}
                onChange={(e, v) => setPage(v)}
                color="primary"
                shape="rounded"
              />
            </Stack>
          </Stack>
        </Card>

        {/* --- MODAL'LAR (DEĞİŞİKLİK YOK) --- */}
        {/* Kupon Oluşturma Modalı */}
        <Dialog
          open={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          fullWidth
          maxWidth="md"
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          {/* Modal içeriği aynı, sadece Oluştur butonuna 'submitting' durumu eklendi */}
          <DialogTitle sx={{ textAlign: 'center', pt: 3 }}>
            <Typography variant="h6" fontWeight={600}>
              KUPON OLUŞTUR
            </Typography>
            <IconButton
              onClick={handleCloseCreateModal}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ py: 3 }}>
            <Typography
              variant="body1"
              fontWeight={600}
              gutterBottom
              borderBottom={'1px solid #EFEFEF'}
              pb={1}
            >
              Kupon Oluşturun
            </Typography>
            <Box>
              <Typography variant="body1" fontWeight={500} color="#344054" sx={{ mb: 2 }}>
                Kupon Bilgileri
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    name="couponName"
                    label="Kupon Adı"
                    value={formData.couponName}
                    onChange={(e) => setFormData({ ...formData, couponName: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    label="Kupon Tipi"
                    value={couponType}
                    onChange={(e) => setCouponType(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="Bireysel">Bireysel</MenuItem>
                    <MenuItem value="Kurumsal">Kurumsal</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    select
                    name="location"
                    label="Lokasyon"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                    fullWidth
                  >
                    {locations.map((loc) => (
                      <MenuItem key={loc.locationCode} value={loc.locationCode}>
                        {`(${loc.locationCode}) - ${loc.name}`}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                {couponType === 'Bireysel' && (
                  <>
                    {' '}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="userFullName"
                        label="Kullanıcı Ad Soyad"
                        value={formData.userFullName}
                        onChange={(e) =>
                          setFormData({ ...formData, userFullName: e.target.value })
                        }
                        fullWidth
                      />
                    </Grid>{' '}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        name="userPhone"
                        type="tel"
                        label="Telefon Numarası"
                        value={formData.userPhone}
                        onChange={handlePhoneChange}
                        fullWidth
                        placeholder="+90 (555) 000-0000"
                        inputProps={{ maxLength: 10 }}
                      />
                    </Grid>{' '}
                  </>
                )}
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Başlangıç Tarihi"
                    value={formData.startDate}
                    onChange={(val) => setFormData({ ...formData, startDate: val })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    sx={{ width: '100%' }}
                    label="Bitiş Tarihi"
                    value={formData.endDate}
                    onChange={(val) => setFormData({ ...formData, endDate: val })}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    name="discountType"
                    label="İndirim Tipi"
                    value={formData.discountType}
                    onChange={(e) => setFormData({ ...formData, discountType: e.target.value })}
                    fullWidth
                  >
                    {mockDiscountTypes.map((d) => (
                      <MenuItem key={d} value={d}>
                        {d}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="discountValue"
                    label="İndirim Değeri"
                    value={formData.discountValue}
                    onChange={(e) => setFormData({ ...formData, discountValue: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="maxUsage"
                    label="Maksimum Kullanım"
                    value={formData.maxUsage}
                    onChange={(e) => setFormData({ ...formData, maxUsage: e.target.value })}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    name="couponCode"
                    label="Kupon Kodu"
                    value={formData.couponCode}
                    onChange={(e) => setFormData({ ...formData, couponCode: e.target.value })}
                    fullWidth
                    disabled={couponType === 'Kurumsal' && isBulkCreate}
                  />
                </Grid>
                {couponType === 'Kurumsal' && (
                  <Grid item xs={12}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isBulkCreate}
                            onChange={(e) => setIsBulkCreate(e.target.checked)}
                            sx={{ color: '#D92D20', '&.Mui-checked': { color: '#D92D20' } }}
                          />
                        }
                        label={
                          <Typography>
                            Toplu kupon oluşturmak istiyorum *
                            <Typography component="span" sx={{ color: '#D92D20' }}>
                              (Her kupon kodu farklı oluşacaktır)
                            </Typography>
                          </Typography>
                        }
                      />
                      {isBulkCreate && (
                        <TextField
                          name="bulkQuantity"
                          label="Adet girin"
                          size="small"
                          value={formData.bulkQuantity}
                          onChange={(e) =>
                            setFormData({ ...formData, bulkQuantity: e.target.value })
                          }
                          sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px' } }}
                        />
                      )}
                    </Stack>
                  </Grid>
                )}
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', p: 3, borderTop: '1px solid #EAECF0' }}>
            <Button
              onClick={handleCloseCreateModal}
              variant="outlined"
              sx={{
                width: '100%',
                maxWidth: '190px',
                color: '#DC423C',
                borderColor: '#DC423C',
                borderRadius: '8px',
                textTransform: 'none',
                px: 2,
                py: 1,
              }}
            >
              Vazgeç
            </Button>
            <Button
              disabled={!isFormValid || submitting}
              onClick={handleCreateCoupon}
              variant="contained"
              sx={{
                width: '100%',
                maxWidth: '190px',
                borderRadius: '8px',
                textTransform: 'none',
                px: 2,
                py: 1,
              }}
            >
              {submitting ? <CircularProgress size={24} color="inherit" /> : 'Oluştur'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isSuccessModalOpen}
          onClose={handleCloseSuccessModal}
          fullWidth
          maxWidth="sm"
          PaperProps={{ sx: { borderRadius: '12px' } }}
        >
          <DialogTitle>
            <IconButton
              onClick={handleCloseSuccessModal}
              sx={{ position: 'absolute', right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <Stack alignItems="center" spacing={2} textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              <Typography variant="h6" fontWeight={600}>
                Kuponlarınız başarıyla oluşturulmuştur.
              </Typography>
              <Typography variant="subtitle1" fontWeight={600}>
                Kupon Listesi
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  width: '100%',
                  maxHeight: 240,
                  overflow: 'auto',
                  p: 2,
                  borderRadius: '8px',
                }}
              >
                <Grid container spacing={1}>
                  {generatedCodes.map((code, index) => (
                    <Grid item xs={6} key={index}>
                      <Typography variant="body2">{`${index + 1}. ${code}`}</Typography>
                    </Grid>
                  ))}
                </Grid>
              </Paper>
              <Button
                variant="outlined"
                onClick={handleCopyCodes}
                startIcon={<ContentCopyIcon />}
                sx={{
                  color: '#344054',
                  borderColor: '#D0D5DD',
                  borderRadius: '8px',
                  textTransform: 'none',
                  width: '100%',
                }}
              >
                Kuponları kopyala
              </Button>
              <Button
                variant="contained"
                onClick={handleCloseSuccessModal}
                fullWidth
                sx={{
                  backgroundColor: '#D92D20',
                  '&:hover': { backgroundColor: '#B42318' },
                  borderRadius: '8px',
                  textTransform: 'none',
                }}
              >
                Tamamla
              </Button>
            </Stack>
          </DialogContent>
        </Dialog>
        <Dialog
          open={isBireyselConfirmOpen}          
          onClose={() => { setBireyselConfirmOpen(false); setSmsInfo(null); }}
          PaperProps={{ sx: { borderRadius: '12px', width: 400, p: 2 } }}
        >
          <DialogTitle sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="h6" fontWeight={600}>
              SMS Gönderme Onayı
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 2, textAlign: 'center' }}>
            <Typography>
              <Typography component="span" fontWeight="bold">
                {smsInfo?.userFullName}
              </Typography>{' '}
              isimli kullanıcıya ait{' '}
              <Typography component="span" fontWeight="bold">
                {smsInfo?.userPhone}
              </Typography>{' '}
              telefon numarasına, oluşturulan{' '}
              <Typography component="span" fontWeight="bold">
                {smsInfo?.couponCode}
              </Typography>{' '}
              kodunu göndermeyi onaylıyor musunuz?
            </Typography>
          </DialogContent>
          <DialogActions sx={{ p: 2, pt: 1 }}>
            <Stack spacing={1.5} width="100%">
              <Button
                fullWidth
                variant="contained"
                onClick={handleConfirmBireyselSend}
                disabled={submitting}
                sx={{
                  backgroundColor: '#D92D20',
                  '&:hover': { backgroundColor: '#B42318' },
                  borderRadius: '8px',
                  textTransform: 'none',
                  py: 1,
                }}
              >
                {submitting ? <CircularProgress size={24} color="inherit" /> : 'Onayla ve Gönder'}
              </Button>
              <Button
                fullWidth
                variant="outlined"                
                onClick={() => { setBireyselConfirmOpen(false); setSmsInfo(null); }}
                sx={{
                  color: '#344054', borderColor: '#D0D5DD', borderRadius: '8px', textTransform: 'none', py: 1,
                }}
              >
                Vazgeç
              </Button>
            </Stack>
          </DialogActions>
        </Dialog>

        <Dialog
          open={isBireyselSuccessOpen}
          onClose={handleCloseBireyselSuccessModal}
          PaperProps={{ sx: { borderRadius: '12px', width: 400 } }}
        >
          <IconButton
            onClick={handleCloseBireyselSuccessModal}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
          <DialogContent sx={{ p: 4 }}>
            <Stack alignItems="center" spacing={2} textAlign="center">
              <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
              <Typography variant="h6" fontWeight={600}>
                Mesajınız karşı tarafa başarıyla iletilmiştir.
              </Typography>
            </Stack>
          </DialogContent>
        </Dialog>

      </Container>
    </LocalizationProvider>
  );
}